import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import useEditorStore from './useEditorStore';
import useFinalizationDataStore from './useFinalizationDataStore';
import { EASING_FUNCTION } from '../lib/EasingFunctions';
import {
  buildVertexAdjacency,
  mergeDraftIntoAppliedZones,
  BLOCKING_PAINT_THRESH,
} from '../lib/blockingZoneMeshUtils';
import {
  capturePreModificationSocket,
  clearPreModificationSocket,
} from '../lib/preModificationSnapshot';

/**
 * The running global-reduction total a chain of Applies adds up to.
 *
 * Percentages compound instead of adding: every Apply works on the socket that
 * is on screen at that moment, so 10% then 10% leaves `1 - 0.9 * 0.9` = 19% of
 * the volume gone, not 20%. Linear offsets ride the vertex normals once each,
 * so those do add.
 *
 * The card locks the unit selector while a chain is live, so `prev` and `next`
 * always share a unit here.
 *
 * @param {{value: number, valueType: string}|null} prev
 * @param {{value: number, valueType: string}} next
 * @returns {{value: number, valueType: string}}
 */
function combineGlobalReduction(prev, next) {
  const prevValue = Number(prev?.value) || 0;
  const nextValue = Number(next?.value) || 0;
  if (!prevValue || prev.valueType !== next.valueType) {
    return { value: nextValue, valueType: next.valueType };
  }
  if (next.valueType === '%') {
    const combined = 1 - (1 - prevValue / 100) * (1 - nextValue / 100);
    return { value: Math.round(combined * 1000) / 10, valueType: '%' };
  }
  return { value: Math.round((prevValue + nextValue) * 1000) / 1000, valueType: next.valueType };
}

const useRectificationStore = create(
  devtools((set, get) => ({
    // Measure Reduction Tool
    measureReduction: {
      measurements: [], // { id, scan { circumference, medioLat, antPost }, rectified { circumference, medioLat, antPost }, percentage, falloff, height }
      currentIndex: 0
    },

    // Scoop/Recharge Tool
    scoopRecharge: {
      enabled: true,
      brushRadius: 1.0,
      paintingMode: false,
      selectedZoneId: 0,
      zones: [],  // { id, name, summitValue, color, points, bezierHandles, influences }
      previewVisible: true,
      previewMesh: null,
      curveResolution: 100, // Default curve sampling resolution
      highAccuracyResolution: 200, // High accuracy resolution for final operations
      showControlsForAllZones: false, // Show curve controls for all zones or just selected
      easing: EASING_FUNCTION.EASE_OUT_CUBIC, // Easing function for gradient deformation
    },

    // Blocking Zone Tool — mask = combined preview for other tools; draftMask = uncommitted paint
    blockingZone: {
      enabled: true,
      radius: 1.0,
      mode: 'paint', // paint, erase
      mask: null,
      draftMask: null,
      appliedZones: [],
      /** Zone ids marked for removal until commitBlockingZoneRemovals */
      pendingZoneRemovalIds: [],
    },

    // Global Reduction Tool
    globalReduction: {
      enabled: true,
      value: 0,          // the amount waiting to be applied, cleared by each Apply
      valueType: '%',
      unit: 'mm',
      previewVisible: true,
      previewMesh: null,
      // Everything the applied chain adds up to, or null while nothing is applied.
      appliedTotal: null,
    },

    // Smoothning Tool
    smoothning: {
      enabled: true,
      zones: [],  // { id, name, smootheningValue, color, isExpanded, influences }
      brushRadius: 0.75,
      previewVisible: true,
      previewMesh: null,
    },

    // Actions
    setMeasureReduction: (data) => {
      set({ measureReduction: { ...get().measureReduction, ...data } })
    },
    setScoopRecharge: (data) => set({ scoopRecharge: { ...get().scoopRecharge, ...data } }),
    setBlockingZone: (data) => {
      const next = { ...get().blockingZone, ...data };
      set({ blockingZone: next });
      if (
        Object.prototype.hasOwnProperty.call(data, 'draftMask') ||
        Object.prototype.hasOwnProperty.call(data, 'appliedZones')
      ) {
        get().recomputeBlockingZoneMask();
      }
    },

    recomputeBlockingZoneMask: () => {
      const bz0 = get().blockingZone;
      const zones = bz0.appliedZones || [];
      const draft = bz0.draftMask;
      const noZones = !zones || zones.length === 0;
      const noDraft = !draft || !draft.length;
      if (noZones && noDraft) {
        set({ blockingZone: { ...get().blockingZone, mask: null } });
        return;
      }
      let n = 0;
      if (draft?.length) n = draft.length;
      else if (zones[0]?.mask?.length) n = zones[0].mask.length;
      if (!n) {
        set({ blockingZone: { ...get().blockingZone, mask: null } });
        return;
      }
      const out = new Float32Array(n);
      for (const z of zones) {
        const m = z.mask;
        if (!m || m.length !== n) continue;
        for (let i = 0; i < n; i++) out[i] = Math.max(out[i], m[i]);
      }
      if (draft && draft.length === n) {
        for (let i = 0; i < n; i++) out[i] = Math.max(out[i], draft[i]);
      }
      let hasAny = false;
      for (let i = 0; i < n; i++) {
        if (out[i] > 1e-4) {
          hasAny = true;
          break;
        }
      }
      const bz = get().blockingZone;
      set({ blockingZone: { ...bz, mask: hasAny ? out : null } });
    },

    applyBlockingZoneDraft: () => {
      const geom = useEditorStore.getState().sceneRefs?.shadedMesh?.geometry;
      const pos = geom?.attributes?.position;
      if (!pos) return;
      const vertexCount = pos.count;
      const bz = get().blockingZone;
      const draft = bz.draftMask;
      if (!draft || draft.length !== vertexCount) return;
      let hasDraft = false;
      for (let i = 0; i < vertexCount; i++) {
        if (draft[i] > BLOCKING_PAINT_THRESH) {
          hasDraft = true;
          break;
        }
      }
      if (!hasDraft) return;

      const adj = buildVertexAdjacency(geom.index, vertexCount);
      const newZones = mergeDraftIntoAppliedZones(draft, bz.appliedZones || [], adj, vertexCount);

      set({
        blockingZone: {
          ...bz,
          appliedZones: newZones,
          draftMask: null,
        },
      });
      get().recomputeBlockingZoneMask();
    },

    toggleBlockingZonePendingRemoval: (zoneId) => {
      const bz = get().blockingZone;
      const pending = new Set(bz.pendingZoneRemovalIds || []);
      if (pending.has(zoneId)) pending.delete(zoneId);
      else pending.add(zoneId);
      set({
        blockingZone: {
          ...bz,
          pendingZoneRemovalIds: Array.from(pending),
        },
      });
    },

    commitBlockingZoneRemovals: () => {
      const bz = get().blockingZone;
      const pending = new Set(bz.pendingZoneRemovalIds || []);
      if (pending.size === 0) {
        get().recomputeBlockingZoneMask();
        return;
      }
      set({
        blockingZone: {
          ...bz,
          appliedZones: bz.appliedZones.filter((z) => !pending.has(z.id)),
          pendingZoneRemovalIds: [],
        },
      });
      get().recomputeBlockingZoneMask();
    },

    clearAllAppliedBlockingZones: () => {
      const bz = get().blockingZone;
      set({
        blockingZone: {
          ...bz,
          appliedZones: [],
          draftMask: null,
          pendingZoneRemovalIds: [],
        },
      });
      get().recomputeBlockingZoneMask();
    },
    setGlobalReduction: (data) => set({ globalReduction: { ...get().globalReduction, ...data } }),
    setSmoothning: (data) => set({ smoothning: { ...get().smoothning, ...data } }),

    //#region Global Reduction Apply/Undo
    isGlobalReductionApplied: false,
    setIsGlobalReductionApplied: (status) => set({ isGlobalReductionApplied: status }),
    isGlobalReductionAppliedFromDraft: false,
    setIsGlobalReductionAppliedFromDraft: (status) => set({ isGlobalReductionAppliedFromDraft: status }),

    currentGlobalReductionCommand: null,
    setCurrentGlobalReductionCommand: (command) => set({ currentGlobalReductionCommand: command }),

    /**
     * Global reduction is re-appliable: each Apply reduces the socket that is on
     * screen right now, so a second pass stacks on the first instead of the card
     * being stuck until Undo.
     *
     * A chain is anchored on its *first* Apply. Every follow-up command reuses
     * that anchor, so a single Undo puts the socket back to before the tool ran
     * at all — which is what undoing this tool means everywhere else: the
     * pre-apply snapshot is write-once, `truncateAtTool` cuts at the first
     * apply, and `resetApplied` returns the card to fully un-applied.
     */
    createGlobalReductionCommand: () => {
      const { globalReduction, currentGlobalReductionCommand, isGlobalReductionApplied } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const preview = globalReduction.previewMesh;
      if (!preview) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalMaterial = sceneRefs.material;

      // First Apply of the chain anchors on the socket as it stands now; a
      // follow-up inherits the anchor the first one captured.
      const anchor = (isGlobalReductionApplied && currentGlobalReductionCommand?.anchor) || {
        geometry: originalMesh.geometry,
        matrix: originalMesh.matrix.clone(),
      };

      const pass = {
        value: parseFloat(globalReduction.value) || 0,
        valueType: globalReduction.valueType,
      };
      const total = combineGlobalReduction(
        isGlobalReductionApplied ? globalReduction.appliedTotal : null,
        pass,
      );
      const unit = globalReduction.unit;

      // Clone geometry and matrix from preview
      const cloneGeom = new THREE.BufferGeometry().copy(preview.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(preview.matrix);

      return {
        anchor,
        execute: () => {
          // Remove preview
          if (preview && preview.isMesh) {
            preview.parent?.remove(preview);
            preview.geometry?.dispose?.();
            preview.material?.dispose?.();
          }

          const newMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          newMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
          newMesh.matrixAutoUpdate = true;
          replaceShadedMesh(newMesh);

          // Save to finalization store — the chain total, not this pass alone.
          useFinalizationDataStore.getState().saveRectificationGlobalReduction(
            total.value,
            total.valueType,
            unit
          );

          get().setIsGlobalReductionApplied(true);
          // The input is cleared so the next Apply has to be a deliberate extra
          // pass rather than an accidental repeat of the one just applied.
          get().setGlobalReduction({
            previewVisible: false,
            previewMesh: null,
            value: 0,
            appliedTotal: total,
          });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(anchor.geometry, originalMaterial);
          restoredMesh.matrix.copy(anchor.matrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          get().setIsGlobalReductionApplied(false);
          get().setGlobalReduction({ appliedTotal: null, previewVisible: true });
        }
      };
    },
    //#endregion

    //#region Rectification Measure 
    rectifiedMeshRef: { current: null }, // Reference to the rectified mesh

    //rectify measure preview
    rectifiedPreview: { enabled: true },
    // add setters
    setRectifiedPreview: (enabled) => set(state => ({
      rectifiedPreview: { ...state.rectifiedPreview, enabled }
    })),

    isRectificationApplied : false,
    setIsRectificationApplied: (status) => set({ isRectificationApplied: status }),
    isRectificationAppliedFromDraft: false,
    setIsRectificationAppliedFromDraft: (status) => set({ isRectificationAppliedFromDraft: status }),

    currentRectificationCommand: null,
    setCurrentRectificationCommand: (command) => set({ currentRectificationCommand: command }),

    createRectificationCommand: () => {
      const { rectifiedMeshRef, measureReduction } = get();

      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

      if (!rectifiedMeshRef.current) return null; // Return null if command can't be created

      // Capture the original mesh state for undo
      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();
      const originalReduction = JSON.parse(JSON.stringify(measureReduction));

      // Store a copy of the rectified mesh for redo operations.
      const cloneGeom = new THREE.BufferGeometry().copy(rectifiedMeshRef.current.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(rectifiedMeshRef.current.matrix);

      // The preview clone's `color` attribute is tinted with band colours for the
      // preview. Restore the socket's TRUE original colours (stashed when the clone
      // was built from the untouched socket) so the baked mesh keeps its own colour
      // instead of the band tint.
      const origColors = rectifiedMeshRef.current.geometry?.userData?.__origColors;
      if (origColors && cloneGeom.attributes.color &&
          cloneGeom.attributes.color.array.length === origColors.length) {
        cloneGeom.attributes.color.array.set(origColors);
        cloneGeom.attributes.color.needsUpdate = true;
      }

      return {
        execute: () => {
          // The socket about to be swapped out IS the "Scan" reference for every
          // measuring tool that reports before/after — record it before it goes.
          capturePreModificationSocket(originalMesh);

          // **CRITICAL FIX**: Ensure geometry has proper spatial structures before applying
          cloneGeom.computeVertexNormals();
          cloneGeom.computeBoundingBox();
          cloneGeom.computeBoundingSphere();
          // Dispose any stale BVH tree (will be rebuilt by tools that need it)
          if (cloneGeom.boundsTree) {
            cloneGeom.disposeBoundsTree();
          }
          
          const rectifiedMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          rectifiedMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          rectifiedMesh.matrix.decompose(rectifiedMesh.position, rectifiedMesh.quaternion, rectifiedMesh.scale);
          rectifiedMesh.matrixAutoUpdate = true;
          replaceShadedMesh(rectifiedMesh);

          if(rectifiedMeshRef.current){
            rectifiedMeshRef.current.parent?.remove(rectifiedMeshRef.current);
            // **CRITICAL FIX**: Dispose BVH tree before disposing geometry
            if (rectifiedMeshRef.current.geometry?.boundsTree) {
              rectifiedMeshRef.current.geometry.disposeBoundsTree();
            }
            rectifiedMeshRef.current.geometry.dispose();
            rectifiedMeshRef.current.material.dispose();
          }
          rectifiedMeshRef.current = null;

          // Save to finalization store
          useFinalizationDataStore.getState().saveRectificationMeasureReduction(
            measureReduction.measurements
          );

          get().setIsRectificationApplied(true);
          get().setRectifiedPreview(false);
          get().setMeasureReduction({ measurements: [], currentIndex: 0 });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          // Nothing is modified any more, so there is no "before" to compare to.
          clearPreModificationSocket();
          get().setIsRectificationApplied(false);
          get().setMeasureReduction({ measurements: originalReduction.measurements, currentIndex: originalReduction.currentIndex });
        }
      };
    },
    //#endregion

    //#region Scoop/Recharge Tool
    isScoopRechargeApplied: false,
    setIsScoopRechargeApplied: (status) => set({ isScoopRechargeApplied: status }),
    isScoopRechargeAppliedFromDraft: false,
    setIsScoopRechargeAppliedFromDraft: (status) => set({ isScoopRechargeAppliedFromDraft: status }),

    currentScoopRechargeCommand: null,
    setCurrentScoopRechargeCommand: (command) => set({ currentScoopRechargeCommand: command }),

    /* Scoop/Recharge Tool */
    createScoopRechargeCommand: () => {
      const { scoopRecharge } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const bakeFromWeights = scoopRecharge.bakeFromWeights;

      if (scoopRecharge.zones.length === 0 || !scoopRecharge.previewMesh || !bakeFromWeights) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();
      const originalScoopRecharge = {
        ...scoopRecharge,
        zones: scoopRecharge.zones.map(zone => ({
          ...zone,
          influences: zone.influences ? new Float32Array(zone.influences) : zone.influences
        }))
      };

      const previewMatrix = new THREE.Matrix4().copy(scoopRecharge.previewMesh.matrix);

      return {
        execute: () => {
          const bakedGeometry = bakeFromWeights({ applySmoothing: true });
          if (!bakedGeometry) return;

          const latestSR = get().scoopRecharge;
          const preview = latestSR?.previewMesh;
          if (preview && preview.isMesh) {
            preview.parent?.remove(preview);
            preview.geometry?.dispose?.();
            preview.material?.dispose?.();
            preview.userData?.baseGeometry?.dispose?.();
          }

          const scoopRechargeMesh = new THREE.Mesh(bakedGeometry, originalMaterial);
          scoopRechargeMesh.matrix.copy(previewMatrix);
          scoopRechargeMesh.matrix.decompose(scoopRechargeMesh.position, scoopRechargeMesh.quaternion, scoopRechargeMesh.scale);
          scoopRechargeMesh.matrixAutoUpdate = true;
          replaceShadedMesh(scoopRechargeMesh);

          // Save to finalization store
          useFinalizationDataStore.getState().saveRectificationScoopRecharge(
            originalScoopRecharge.zones
          );

          get().setIsScoopRechargeApplied(true);
          get().setScoopRecharge({
            zones: [],
            previewVisible: false,
            previewMesh: null
          });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          get().setIsScoopRechargeApplied(false);
          // Restore the complete original state including zones with their influences
          // Note: previewMesh is set to null instead of originalScoopRecharge.previewMesh
          // because the preview was disposed during execute(). Setting it to null
          // forces ensurePreview() to recreate it from the restored shadedMesh.
          get().setScoopRecharge({
            zones: originalScoopRecharge.zones,
            previewVisible: originalScoopRecharge.previewVisible,
            previewMesh: null, // Force recreation instead of restoring disposed reference
            brushRadius: originalScoopRecharge.brushRadius,
            selectedZoneId: originalScoopRecharge.selectedZoneId
          });
        }
      };
    },
    //#endregion

    //#region Smoothning Tool
    isSmootheningApplied: false,
    setIsSmootheningApplied: (status) => set({ isSmootheningApplied: status }),
    isSmootheningAppliedFromDraft: false,
    setIsSmootheningAppliedFromDraft: (status) => set({ isSmootheningAppliedFromDraft: status }),

    currentSmootheningCommand: null,
    setCurrentSmootheningCommand: (command) => set({ currentSmootheningCommand: command }),
    
    createSmootheningCommand: () => {
      const { smoothning } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

      if(smoothning.zones.length === 0 || !smoothning.previewMesh) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();

      // Deep clone the smoothning state including zones with their influences
      const originalSmoothning = {
        ...smoothning,
        zones: (smoothning.zones || []).map(zone => ({
          ...zone,
          influences: zone.influences ? new Float32Array(zone.influences) : zone.influences
        }))
      };

      // Clone the preview geometry and transform
      const cloneGeom = new THREE.BufferGeometry().copy(smoothning.previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(smoothning.previewMesh.matrix);

      return {
        execute: () => {
          // Remove and dispose any lingering preview mesh before replacing shaded mesh
          const latest = get().smoothning;
          const preview = latest?.previewMesh;
          if (preview && preview.isMesh) {
            preview.parent?.remove(preview);
            preview.geometry?.dispose?.();
            preview.material?.dispose?.();
            preview.userData?.baseGeometry?.dispose?.();
          }

          const smoothedMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          smoothedMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          smoothedMesh.matrix.decompose(smoothedMesh.position, smoothedMesh.quaternion, smoothedMesh.scale);
          smoothedMesh.matrixAutoUpdate = true;
          replaceShadedMesh(smoothedMesh);

          // Save to finalization store
          useFinalizationDataStore.getState().saveRectificationSmoothening(
            originalSmoothning.zones
          );

          get().setIsSmootheningApplied(true);
          get().setSmoothning({
            zones: [],
            previewVisible: false,
            previewMesh: null
          });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          get().setIsSmootheningApplied(false);
          // Restore the complete original state including zones with their influences
          // Note: previewMesh is set to null instead of originalSmoothning.previewMesh
          // because the preview was disposed during execute(). Setting it to null
          // forces ensurePreview() to recreate it from the restored shadedMesh.
          get().setSmoothning({
            zones: originalSmoothning.zones,
            previewVisible: originalSmoothning.previewVisible,
            previewMesh: null, // Force recreation instead of restoring disposed reference
            brushRadius: originalSmoothning.brushRadius
          });
        }
      };
    },
    //#endregion

    // Reset all data
    reset: () => set({
      measureReduction: {
        measurements: [],
        currentIndex: 0
      },
      scoopRecharge: { enabled: true, brushRadius: 1.0, paintingMode: false, selectedZoneId: 0, zones: [], easing: EASING_FUNCTION.EASE_OUT_SINE },
      blockingZone: {
        enabled: true,
        radius: 5.0,
        mode: 'paint',
        mask: null,
        draftMask: null,
        appliedZones: [],
        pendingZoneRemovalIds: [],
      },
      globalReduction: { enabled: true, value: 0, valueType: '%', unit: 'mm', previewVisible: true, previewMesh: null, appliedTotal: null },
      smoothning: { enabled: true, zones: [], brushRadius: 1.0 },
    })
  }))
);

export default useRectificationStore;